import {useCallback, useEffect, useMemo} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {useActivityTrigger} from 'hooks/useActivityTrigger'
import {useHistoryBack} from 'hooks/useHistoryBack'
import {
  getIsAlternativeOffersOverlayOpen,
  getIsAnyOfferCheckCallStarted
} from 'modules/alternativeOffers/selectors'
import {getOverlay, getOverlayVisibility} from 'modules/overlay/selectors'
import {OverlayType} from 'modules/overlay/types'
import {
  getPopupOnExitShownState,
  getPopupOnExitVisibility,
  hidePopupOnExit,
  showPopupOnExit
} from 'modules/popupOnExit/slice'
import {getIsUserLandingDirectlyOnSrp} from 'modules/search/selectors'
import {setPopupOnExitShown} from 'utils/persistence'

import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {useBrandCapabilities} from '@vio/core/src/_web/brand/hooks/useBrandCapabilities'
import {useDispatchTeamEvent} from '@vio/core/src/analytics/hooks/useDispatchTeamEvent'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {Action, Category, Entity} from '@vio/core/src/analytics/types/Events'
import {Page} from '@vio/core/src/analytics/types/Events'
import {
  getIsAuthenticated,
  isPasswordlessLoginVisible
} from '@vio/core/src/auth/modules/selectors'
import {useToggle} from '@vio/core/src/experiments/hooks/useToggle'
import {getIsGhaChecker} from '@vio/core/src/rateChecker/selectors'
import {getIsEmailSignInOpen} from '@vio/shared/src/authentication/Email/slice/selectors'

import {useExitIntentDetection} from './useExitIntentDetection'

export type ExitPopupPage = `${Page.Search}` | `${Page.Accommodation}`

export type ExitPopupVariant = 'deal_freeze' | 'standard'

/**
 * Hook that manages the exit popup behavior for SRP and AP
 * It handles showing/hiding the popup based on user exit intent and various conditions.
 *
 * @param page - The page where the exit popup is being used ('Search' or 'Accommodation')
 * @param bypassAuthAndDirectMobile - When true, skip the auth short-circuit and the
 *   mobile `isDirectSrpUser` gate. Used by the Deal Freeze SRP exit-popup A/B test
 *   to widen the variant audience (logged-in + internal-funnel mobile).
 * @param variant - Tagged on Displayed/Dismissed analytics so the experiment can
 *   compare arms.
 */

export const useExitPopup = ({
  page,
  isEnabled,
  bypassAuthAndDirectMobile = false,
  variant = 'standard'
}: {
  page: ExitPopupPage
  isEnabled: boolean
  bypassAuthAndDirectMobile?: boolean
  variant?: ExitPopupVariant
}) => {
  const dispatch = useDispatch()
  const toggle = useToggle()
  const isPopupOnExitShown = useSelector(getPopupOnExitShownState)
  const isPasswordlessPopupVisible = useSelector(isPasswordlessLoginVisible)
  const isOpen = useSelector(getPopupOnExitVisibility)
  const overlayState = useSelector(getOverlay)
  const dispatchTeamEvent = useDispatchTeamEvent()
  const isPriceWatchOverlayShown = useSelector(state =>
    getOverlayVisibility(state, OverlayType.PriceWatchOverlay)
  )
  const isEmailSignUpOverlayShown = useSelector(getIsEmailSignInOpen)

  const isDirectSrpUser = useSelector(getIsUserLandingDirectlyOnSrp)
  const {isMobile} = useDeviceLayout()

  const isAlternativeOffersOverlayOpen = useSelector(
    getIsAlternativeOffersOverlayOpen
  )

  const isAnyOfferCheckCallPending = useSelector(getIsAnyOfferCheckCallStarted)
  const isAuthenticated = useSelector(getIsAuthenticated)
  const isGhaChecker = useSelector(getIsGhaChecker)
  const {
    retention: {hasExitPopup}
  } = useBrandCapabilities()

  const isExitPopupEnabled = useMemo(() => {
    if (!hasExitPopup) {
      return false
    }
    if (isGhaChecker) {
      return false
    }
    if (!bypassAuthAndDirectMobile && isAuthenticated) {
      return false
    }
    switch (page) {
      case Page.Search:
        if (isMobile) {
          return (
            (bypassAuthAndDirectMobile || isDirectSrpUser) &&
            !isPopupOnExitShown &&
            !overlayState.length &&
            !isPriceWatchOverlayShown &&
            !isEmailSignUpOverlayShown &&
            isEnabled
          )
        }
        return (
          isEnabled &&
          !isPopupOnExitShown &&
          !isPasswordlessPopupVisible &&
          !overlayState?.length &&
          !isEmailSignUpOverlayShown
        )
      case Page.Accommodation:
        return (
          !overlayState.length &&
          !isPopupOnExitShown &&
          !isEmailSignUpOverlayShown &&
          isEnabled &&
          !isAlternativeOffersOverlayOpen &&
          !isAnyOfferCheckCallPending
        )
      default:
        return false
    }
  }, [
    isAlternativeOffersOverlayOpen,
    isAnyOfferCheckCallPending,
    isDirectSrpUser,
    isMobile,
    isPasswordlessPopupVisible,
    isPopupOnExitShown,
    isPriceWatchOverlayShown,
    overlayState.length,
    isEmailSignUpOverlayShown,
    page,
    isEnabled,
    hasExitPopup,
    isAuthenticated,
    isGhaChecker,
    bypassAuthAndDirectMobile
  ])

  const openPopup = useCallback(() => {
    dispatch(showPopupOnExit())
    dispatchTeamEvent(
      trackEvent({
        category: Category.System,
        entity: Entity.PopupOnExit,
        action: Action.Displayed,
        payload: {variant}
      })
    )
    setPopupOnExitShown()
  }, [dispatch, dispatchTeamEvent, variant])

  // User-initiated close. Wire to PopUp's `closeAction`.
  const dismissPopup = useCallback(() => {
    dispatch(hidePopupOnExit())
  }, [dispatch])

  // Programmatic close (e.g. auto-close on search error, close-before-nav from CTA).
  // No analytics — use dismissPopup for user-initiated closes.
  const closePopup = useCallback(() => {
    dispatch(hidePopupOnExit())
  }, [dispatch])

  useExitIntentDetection({
    isEnabled: isExitPopupEnabled,
    onExitIntent: openPopup
  })

  const onMobileBack = useCallback(() => {
    dispatch(
      trackEvent({
        category: Category.User,
        entity: Entity.BrowserBackButton,
        action: Action.Clicked
      })
    )
    openPopup()
  }, [dispatch, openPopup])

  // Audience targeting (Vio brands / mobile / Meta traffic) is configured in Optimizely.
  // Variation `b` swaps the back-button trigger for an activity-based trigger to comply
  // with Google's back-button hijacking policy (enforced 15 June 2026).
  const isActivityTriggerVariant = toggle(
    '0bea45b9-activity-exit-popup',
    false,
    true
  )

  // on mobile, we want to open the popup when the user clicks the back button
  // this is currently only enabled on SRP
  useHistoryBack(
    isExitPopupEnabled && isMobile && !isActivityTriggerVariant,
    onMobileBack
  )

  useActivityTrigger({
    enabled: isExitPopupEnabled && isMobile && isActivityTriggerVariant,
    onTrigger: openPopup
  })

  useEffect(() => {
    isPriceWatchOverlayShown && closePopup()
  }, [isPriceWatchOverlayShown, closePopup])

  return {closePopup, dismissPopup, isOpen}
}
